import axios from 'axios'
import {
  clearAuthSession,
  getAuthApiBaseUrl,
  getCurrentApiKeyExpirationMs,
  hasAuthenticatedSession,
  hasUsableAuthenticatedSession,
  storeAuthenticatedSession,
  storeAuthRedirectNotice,
  getSelectedCompanySlug
} from './session'

export class SessionExpiredError extends Error {
  constructor(message = 'SESSION_EXPIRED') {
    super(message)
    this.name = 'SessionExpiredError'
  }
}

const DEFAULT_SESSION_EXPIRED_NOTICE = {
  title: 'Sessão expirada',
  message:
    'Sua sessão expirou ou ficou inválida. Faça login novamente para continuar.'
}

type RefreshTokenResponse = {
  token?: string
  api_key?: string
  refresh_token?: string
  refreshToken?: {
    token?: string
  }
}

let refreshSessionPromise: Promise<boolean> | null = null
let redirectInProgress = false
let preventiveRefreshTimeout: number | null = null

const PREVENTIVE_REFRESH_LEAD_MS = 2 * 60 * 1000
const MIN_REFRESH_DELAY_MS = 5 * 1000

const getStoredRefreshToken = (): string =>
  `${window.sessionStorage.getItem('refreshToken') || ''}`.trim()

const getStoredCompanyId = (): string =>
  `${window.sessionStorage.getItem('company_id') || window.sessionStorage.getItem('company') || window.sessionStorage.getItem('originCompany') || ''}`.trim()

const getStoredRole = (): string =>
  `${window.sessionStorage.getItem('role') || ''}`.trim()

const getStoredRoleId = (): string =>
  `${window.sessionStorage.getItem('role_id') || ''}`.trim()

const getStoredLoginId = (): string =>
  `${window.sessionStorage.getItem('login_id') || ''}`.trim()

const getStoredName = (): string =>
  `${window.sessionStorage.getItem('name') || ''}`.trim()

const resolveTokenFromRefresh = (payload: RefreshTokenResponse): string =>
  `${payload?.token || payload?.api_key || ''}`.trim()

const resolveRefreshTokenFromRefresh = (payload: RefreshTokenResponse): string =>
  `${payload?.refresh_token || payload?.refreshToken?.token || ''}`.trim()

export const redirectToLoginWithSessionNotice = (
  message = DEFAULT_SESSION_EXPIRED_NOTICE.message,
  title = DEFAULT_SESSION_EXPIRED_NOTICE.title
): void => {
  if (redirectInProgress) {
    return
  }

  redirectInProgress = true

  if (preventiveRefreshTimeout) {
    window.clearTimeout(preventiveRefreshTimeout)
    preventiveRefreshTimeout = null
  }

  clearAuthSession()
  storeAuthRedirectNotice({ title, message })
  const selectedSlug = `${getSelectedCompanySlug() || ''}`.trim()
  const loginUrl = selectedSlug ? `/?cmp=${selectedSlug}` : '/'

  window.location.replace(loginUrl)
}

export const stopPreventiveSessionRefresh = (): void => {
  if (preventiveRefreshTimeout) {
    window.clearTimeout(preventiveRefreshTimeout)
    preventiveRefreshTimeout = null
  }
}

export const schedulePreventiveSessionRefresh = (): void => {
  stopPreventiveSessionRefresh()

  if (!hasAuthenticatedSession()) {
    return
  }

  const expirationMs = getCurrentApiKeyExpirationMs()

  if (!expirationMs) {
    return
  }

  const refreshDelayMs = Math.max(
    expirationMs - Date.now() - PREVENTIVE_REFRESH_LEAD_MS,
    MIN_REFRESH_DELAY_MS
  )

  preventiveRefreshTimeout = window.setTimeout(async () => {
    preventiveRefreshTimeout = null

    if (!hasAuthenticatedSession()) {
      return
    }

    const recovered = await recoverAuthenticatedSession()

    if (!recovered) {
      return
    }
  }, refreshDelayMs)
}

export const syncPreventiveSessionRefresh = (): void => {
  if (!hasAuthenticatedSession()) {
    stopPreventiveSessionRefresh()
    return
  }

  schedulePreventiveSessionRefresh()
}

export const recoverAuthenticatedSession = async (): Promise<boolean> => {
  if (!hasAuthenticatedSession()) {
    return false
  }

  const recovered = await refreshAuthenticatedSession()

  if (recovered) {
    return true
  }

  redirectToLoginWithSessionNotice()
  return false
}

export const refreshAuthenticatedSession = async (): Promise<boolean> => {
  if (refreshSessionPromise) {
    return refreshSessionPromise
  }

  refreshSessionPromise = (async () => {
    if (!hasAuthenticatedSession()) {
      return false
    }

    const refreshToken = getStoredRefreshToken()
    const tenantId = `${window.sessionStorage.getItem('tenant') || ''}`.trim()

    if (!refreshToken || !tenantId) {
      return false
    }

    const response = await axios.post<RefreshTokenResponse>(
      `${getAuthApiBaseUrl()}/auth/refresh-token`,
      {},
      {
        headers: {
          'x-refresh-token': refreshToken,
          'x-tenant-id': tenantId
        },
        validateStatus: () => true
      }
    )

    if (response.status < 200 || response.status >= 300) {
      return false
    }

    const payload = response.data || {}
    const apiKey = resolveTokenFromRefresh(payload)
    const nextRefreshToken = resolveRefreshTokenFromRefresh(payload)

    if (!apiKey) {
      return false
    }

    storeAuthenticatedSession({
      apiKey,
      refreshToken: nextRefreshToken || refreshToken,
      tenantId,
      companyId: getStoredCompanyId(),
      role: getStoredRole() || 'default',
      roleId: getStoredRoleId(),
      loginId: getStoredLoginId(),
      name: getStoredName() || 'Verne'
    })

    schedulePreventiveSessionRefresh()

    return true
  })()
    .catch(() => false)
    .finally(() => {
      refreshSessionPromise = null
    })

  return refreshSessionPromise
}
